import React, { useEffect, useMemo, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

/**
 * Projects with uniform-height cards + modal popups.
 * - Cards keep same height on all slides
 * - "Learn More" opens a warm, easy-to-read modal for the first two projects
 * - Disabled for "Coming Soon"
 */
const Projects = () => {
  const content = useMemo(
    () => [
      {
        key: "music-hut",
        img: "images/slideshow/4.jpg",
        title: "MUSIC HUT",
        date: "NOV 2025",
        description:
          "Music Hut is a creative space where young people come together to explore sound, rhythm, and self-expression. Based in St Ann’s and run in partnership with The Hope Hut, our sessions blend music, sustainability, and digital creativity.",
      },
      // {
      //   key: "waste-nothing",
      //   img: "images/guitar.jpg",
      //   title: "WASTE NOTHING PLAY ON",
      //   date: "SEP 2025",
      //   description:
      //     "Waste Nothing, Play On is our community-led initiative all about caring for our parks, green spaces, and neighbourhoods while getting creative in the process. We believe that what looks like junk can be reimagined into something joyous, fun, and musical.",
      // },
      {
        key: "up-beatz",
        img: "images/comingsoon.png",
        title: "UP BEATZ",
        date: "2026",
        description: "",
      },
    ],
    []
  );

  // Modal state
  const [openKey, setOpenKey] = useState(null);

  // Modal body copy
  const modalCopy = {
    "music-hut": {
      heading: "Music Hut",
      sub: "Creative chaos — one beat at a time.",
      body: [
        "Music Hut is a creative space where young people come together to explore sound, rhythm, and self-expression. Based in St Ann’s and run in partnership with The Hope Hut, our sessions blend music, sustainability, and digital creativity.",
        // "Each week, we use upcycled and everyday materials to create instruments, make beats, and learn production skills — all while keeping our local spaces clean through our Waste Nothing, Play On initiative.",
        "Whether you’re into making music, learning digital tools, or just want to meet other creative young people, Music Hut is all about connection, creativity, and community.",
      ],
      details: ["🕔 Every Thursday, 5–7 PM", "📍 Hope Hut, St Ann’s, Nottingham"],
    },
    "waste-nothing": {
      heading: "Waste Nothing, Play On",
      sub: "Turning “junk” into joy",
      body: [
        "Waste Nothing, Play On is our community-led initiative all about caring for our parks, green spaces, and neighbourhoods while getting creative in the process. We believe that what looks like junk can be reimagined into something joyous, fun, and musical.",
        "Through community cleanups and creative reuse projects, we collect discarded materials and turn them into instruments, art, and new ideas. From bottle shakers to tin-can drums, every sound we make reminds us that sustainability and creativity go hand in hand.",
        "It is about taking pride in where we live, connecting with others, and proving that small acts like picking up litter or repurposing waste can spark big change.",
        "Join us, get involved, and help us turn junk into joy.",
      ],
      details: [],
    },
  };

  const swiperRef = useRef(null);
  const [allowAutoplay, setAllowAutoplay] = useState(true);

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      const handler = () => setAllowAutoplay(!mq.matches);
      handler();
      mq.addEventListener?.("change", handler);
      return () => mq.removeEventListener?.("change", handler);
    }
  }, []);

  // Close modal on ESC
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") setOpenKey(null);
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, []);

  // Helpers
  const isComingSoon = (item) => !item.description || item.description.trim() === "";
  const canOpen = (item) => !isComingSoon(item) && modalCopy[item.key];

  return (
    <section
      id="projects"
      className="relative py-12 md:py-16 px-6 md:px-10 bg-[linear-gradient(180deg,#FFF6E3_0%,#FFE8BA_100%)]"
    >
      {/* Decorative background accents */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-10 -left-10 h-56 w-56 rounded-full blur-3xl opacity-30 bg-[radial-gradient(circle,_rgba(245,158,11,0.18),transparent_60%)]" />
        <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full blur-3xl opacity-30 bg-[radial-gradient(circle,_rgba(234,88,12,0.18),transparent_60%)]" />
      </div>

      <div className="mx-auto max-w-7xl">
        <h1 className="text-center text-neutral-900 text-[36px] md:text-[44px] font-extrabold tracking-tight bg-white/70 backdrop-blur ring-1 ring-amber-200 shadow-sm rounded-2xl px-5 py-3">
          Projects
        </h1>

        <div className="mt-8 md:mt-10">
          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            modules={[Navigation, Autoplay, Pagination]}
            slidesPerView={2.6}
            spaceBetween={20}
            navigation
            pagination={{ clickable: true }}
            loop={content.length > 2}
            grabCursor
            breakpoints={{
              320: { slidesPerView: 1, spaceBetween: 12 },
              640: { slidesPerView: 1.4, spaceBetween: 14 },
              768: { slidesPerView: 2, spaceBetween: 16 },
              1024: { slidesPerView: 2, spaceBetween: 18 },
              1440: { slidesPerView: 2, spaceBetween: 20 },
            }}
            autoplay={
              allowAutoplay
                ? {
                    delay: 3800,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: false,
                  }
                : false
            }
            className="w-full max-w-[2000px]"
            onMouseEnter={() => {
              if (allowAutoplay && swiperRef.current?.autoplay) {
                swiperRef.current.autoplay.stop();
              }
            }}
            onMouseLeave={() => {
              if (allowAutoplay && swiperRef.current?.autoplay) {
                swiperRef.current.autoplay.start();
              }
            }}
          >
            {content.map((el, i) => (
              <SwiperSlide key={`${el.title}-${i}`} className="!h-auto">
                <article
                  className="
                    group w-full 
                    bg-white/90 backdrop-blur-sm ring-1 ring-neutral-200/70 shadow-xl 
                    rounded-3xl overflow-hidden 
                    flex flex-col
                    h-[28rem] md:h-[32rem]   /* UNIFORM CARD HEIGHT */
                    transition-transform duration-300 hover:-translate-y-0.5
                  "
                >
                  {/* Media: 40% */}
                  <div className="relative w-full h-[40%] overflow-hidden">
                    <img
                      src={el.img}
                      alt={el.title}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute top-3 left-3 md:top-4 md:left-4">
                      <span className="inline-flex items-center rounded-full bg-white/90 text-neutral-900 text-xs md:text-sm font-semibold px-3 py-1 ring-1 ring-neutral-200">
                        {el.date}
                      </span>
                    </div>
                    {isComingSoon(el) && (
                      <div className="absolute top-3 right-3 md:top-4 md:right-4">
                        <span className="inline-flex items-center rounded-full bg-gradient-to-r from-rose-500 to-orange-400 text-white text-xs md:text-sm font-semibold px-3 py-1 shadow-sm">
                          Coming Soon
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Body: 60% */}
                  <div className="flex h-[60%] flex-col gap-4 p-6 md:p-8">
                    <header className="text-center">
                      <h2 className="text-[22px] md:text-[26px] font-extrabold tracking-tight text-neutral-900">
                        {el.title}
                      </h2>
                      <div className="mx-auto mt-2 h-[2px] w-24 bg-neutral-900/80 rounded-full" />
                    </header>

                    <p className="text-[16px] md:text-[18px] leading-snug text-neutral-700 text-center mx-auto max-w-[60ch] line-clamp-5">
                      {isComingSoon(el)
                        ? "Details are on the way. Stay tuned for updates!"
                        : el.description}
                    </p>

                    <div className="mt-auto flex justify-center gap-3">
                      <button
                        onClick={() => setOpenKey(canOpen(el) ? el.key : null)}
                        disabled={!canOpen(el)}
                        className={`rounded-full px-4 py-2 text-sm font-semibold transition shadow-sm ${
                          canOpen(el)
                            ? "text-white bg-neutral-900 hover:bg-neutral-800"
                            : "bg-neutral-200 text-neutral-500 cursor-not-allowed"
                        }`}
                      >
                        Learn More
                      </button>
                      <a
                        href="#contact"
                        className="rounded-full px-4 py-2 text-sm font-semibold ring-1 ring-amber-300 text-amber-900 bg-amber-50 hover:bg-amber-100 transition"
                      >
                        Get Involved
                      </a>
                    </div>
                  </div>
                </article>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      <div className="mx-auto mt-10 h-px max-w-7xl bg-gradient-to-r from-transparent via-neutral-300/50 to-transparent" />

      {/* ===== Modal (only for Music Hut & Waste Nothing) ===== */}
      {openKey && (
        <Modal onClose={() => setOpenKey(null)}>
          <ProjectModalBody onClose={() => setOpenKey(null)} content={modalCopy[openKey]} />
        </Modal>
      )}
    </section>
  );
};

/* ---------- Modal Shell ---------- */
const Modal = ({ children, onClose }) => {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed  inset-0 z-[100] flex items-end sm:items-center justify-center"
    >
      {/* Backdrop */}
      <button
        aria-label="Close modal"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />
      {/* Panel */}
      <div className="relative w-full sm:max-w-3xl mx-auto sm:mx-4 bg-white/95 backdrop-blur rounded-t-3xl sm:rounded-3xl ring-1 ring-amber-200 shadow-2xl">
        <div className="absolute right-3 top-3">
          <button
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-50 text-amber-900 ring-1 ring-amber-200 hover:bg-amber-100 transition"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className=" p-6 sm:p-8">{children}</div>
      </div>
    </div>
  );
};

/* ---------- Modal Body Content ---------- */
const ProjectModalBody = ({ content, onClose }) => {
  if (!content) return null;
  const { heading, sub, body = [], details = [] } = content;

  return (
    <div>
      <h3 className="text-[24px] sm:text-[28px] font-extrabold tracking-tight text-neutral-900">
        {heading}
      </h3>
      {sub && <p className="mt-1 text-[16px] sm:text-[18px] text-amber-800">{sub}</p>}

      <div className="mt-5 space-y-4 text-justify">
        {body.map((p, i) => (
          <p key={i} className="text-[16px] sm:text-[18px] leading-relaxed text-neutral-800">
            {p}
          </p>
        ))}
      </div>

      {details.length > 0 && (
        <div className="mt-6 rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-4 sm:p-5">
          <ul className="space-y-1 text-amber-900 font-medium">
            {details.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <a
          onClick={onClose}
          // href="#contact"
          className="rounded-full px-5 py-2.5 text-sm font-semibold text-white bg-neutral-900 hover:bg-neutral-800 transition shadow-sm"
        >
          Get Involved
        </a>
        <a
          href="#contact"
          className="rounded-full px-5 py-2.5 text-sm font-semibold ring-1 ring-amber-300 text-amber-900 bg-amber-50 hover:bg-amber-100 transition"
        >
          Learn More About Us
        </a>
      </div>
    </div>
  );
};

export default Projects;
